import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../config/theme';
import { rem } from 'polished';
import { Body1, Body2, Caption, Head1, Head4 } from '../Typography/Typography';
import Button from '../Button/Button';
import PrismicLink from '../PrismicLink/PrismicLink';
import Box from '../Box/Box';
import InfoIcon from '../../images/icons/info.svg';
import Tooltip from '../Tooltip/Tooltip';
import Image from '../Image/Image';
import Stack from '../Stack/Stack';
import get from 'lodash/get';

const StyledCard = styled(Box)`
    background-color: ${theme.colors.white};
    border: ${({ borderColor, theme }) =>
        `2px solid ${get(theme.colors, borderColor)}`};
    border-top-width: 7px;
    padding: ${rem(32)} ${rem(24)};
    display: grid;
    grid-gap: ${rem(16)};
    align-content: start;
    position: relative;
`;

const PricingCard = ({ plan }) => {
    const {
        title,
        advanced_features_is_beta,
        advanced_features_title,
        caption,
        price,
        cta_link,
        cta_text,
        cta_tracking_event,
        description,
        features_title,
        is_selected,
        features,
        advanced_features,
        border_color,
    } = plan;

    return (
        <StyledCard borderColor={border_color}>
            <Head4 textAlign="center">{title}</Head4>
            <Box as={Body1} textAlign="center">
                {description}
            </Box>

            <Stack space={rem(8)} py={rem(8)}>
                <PrismicLink link={cta_link}>
                    <Button
                        variant={is_selected ? 'primary' : 'secondary'}
                        size="lg"
                        fullWidth
                        trackingEvent={cta_tracking_event}
                    >
                        {cta_text}
                    </Button>
                </PrismicLink>
            </Stack>
            <Box>
                <Body2 bold marginTop={rem(8)} marginBottom={rem(16)}>
                    {features_title}
                </Body2>
                <Box display="grid" gridGap={rem(8)}>
                    {features.map((feature) => {
                        return (
                            <Box
                                display="grid"
                                gridAutoFlow="column"
                                alignItems="center"
                                justifyContent="start"
                            >
                                <Box
                                    width={rem(24)}
                                    height={rem(24)}
                                    as={Image}
                                    img={feature.icon}
                                    marginRight={rem(8)}
                                    alt="pricing plan feature icon"
                                />
                                <Body1>{feature.text}</Body1>
                                {feature.tooltip_text && (
                                    <Box
                                        display="inline-flex"
                                        cursor="pointer"
                                        marginLeft={rem(4)}
                                    >
                                        <Tooltip
                                            content={
                                                <Tooltip.Content>
                                                    {feature.tooltip_text}
                                                </Tooltip.Content>
                                            }
                                        >
                                            <InfoIcon />
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {advanced_features.length > 0 && (
                    <Box marginTop={rem(32)}>
                        <Body2
                            as={Box}
                            bold
                            paddingY={rem(2)}
                            paddingX={rem(8)}
                            backgroundColor={theme.colors.tertiary.purple[100]}
                            borderRadius={theme.radii.sm}
                            display="inline-flex"
                            alignItems="center"
                            marginBottom={rem(16)}
                            color={theme.colors.tertiary.purple[1700]}
                            marginLeft={rem(-8)}
                        >
                            {advanced_features_title}
                            {advanced_features_is_beta && (
                                <Caption
                                    marginLeft={rem(4)}
                                    color={theme.colors.tertiary.purple[1100]}
                                >
                                    BETA
                                </Caption>
                            )}
                        </Body2>
                        <Box display="grid" gridGap={rem(8)}>
                            {advanced_features.map((feature) => {
                                return (
                                    <Box
                                        display="grid"
                                        gridAutoFlow="column"
                                        alignItems="start"
                                        justifyContent="start"
                                    >
                                        <Box
                                            width={rem(24)}
                                            height={rem(24)}
                                            as={Image}
                                            img={feature.icon}
                                            marginRight={rem(8)}
                                            alt="pricing plan feature icon"
                                        />
                                        <Body2>{feature.text}</Body2>
                                        {feature.tooltip_text && (
                                            <Box
                                                display="inline-flex"
                                                cursor="pointer"
                                                marginLeft={rem(4)}
                                                marginTop={rem(3)}
                                            >
                                                <Tooltip
                                                    content={
                                                        <Tooltip.Content>
                                                            {
                                                                feature.tooltip_text
                                                            }
                                                        </Tooltip.Content>
                                                    }
                                                >
                                                    <InfoIcon />
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </StyledCard>
    );
};

export default PricingCard;
